import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { UserIcon, UsersIcon, BookOpenIcon, ChatBubbleLeftRightIcon, ArrowTrendingUpIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('individuals');

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-dark text-white">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80"
            alt="A diverse group of people learning together"
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        <div className="relative container mx-auto px-6 py-32 text-center">
          <img 
            src="/images/TOM-logo-512.png" 
            alt="Together On Mission Logo"
            className="h-20 w-auto mx-auto mb-8"
          />
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4 tracking-tight">
            Go Make Disciples.
          </h1>
          <p className="text-lg md:text-xl mb-10 text-gray-300 max-w-3xl mx-auto">
            A private, secure platform for individuals and communities to intentionally nurture spiritual growth, share stories of faith, and build mentoring relationships that last.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
            >
              Start Your Journey
            </Link>
            <Link
              to="/create-organization"
              className="inline-block bg-secondary text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
            >
              Empower Your Community
            </Link>
          </div>
        </div>
      </section>

      {/* For You Section (Tabs) */}
      <section id="for-you" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-dark">A Platform for Growth</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Whether you're growing on your own or leading others, we provide the tools you need to make discipleship intentional.
          </p>
          <div className="w-full max-w-md mx-auto bg-gray-100 rounded-full p-1.5 flex mb-12">
            <button
              onClick={() => setActiveTab('individuals')}
              className={`w-1/2 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'individuals' ? 'bg-white text-dark shadow' : 'text-gray-500 hover:bg-white/60'}`}
            >
              For Individuals
            </button>
            <button
              onClick={() => setActiveTab('communities')}
              className={`w-1/2 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'communities' ? 'bg-white text-dark shadow' : 'text-gray-500 hover:bg-white/60'}`}
            >
              For Communities
            </button>
          </div>

          {/* Individual's Content */}
          {activeTab === 'individuals' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
              {featuresForIndividuals.map((feature) => (
                <div key={feature.title} className="text-center p-6">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mx-auto mb-4">
                    <feature.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark">{feature.title}</h3>
                  <p className="mt-2 text-base text-gray-500">{feature.description}</p>
                </div>
              ))}
            </div>
          )}

          {/* Communities Content */}
          {activeTab === 'communities' && (
            <div className="animate-fade-in">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuresForCommunities.map((feature) => (
                  <div key={feature.title} className="text-center p-6">
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-secondary/10 mx-auto mb-4">
                      <feature.icon className="h-8 w-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold text-dark">{feature.title}</h3>
                    <p className="mt-2 text-base text-gray-500">{feature.description}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/for-churches"
                  className="inline-block bg-secondary text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition duration-300"
                >
                  See the Full Church Feature Guide &rarr;
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mission Statement with Image */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80"
                alt="Two men studying the Bible together at a table"
                className="rounded-2xl shadow-xl aspect-[4/3] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-dark mb-6">Go and Make Disciples.</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our mission is to help you fulfill yours. We handle the organizational details so you can focus on what matters most—building meaningful relationships and guiding others in their walk with Christ.
              </p>
              <Link
                to="/signup"
                className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition duration-300"
              >
                Start Your Mission
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16 text-dark">
            Trusted by Leaders and Mentors
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.name} className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <p className="text-gray-600 mb-6 italic">"{testimonial.quote}"</p>
                <div className="font-semibold text-dark">{testimonial.name}</div>
                <div className="text-primary">{testimonial.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-dark text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Answer the Call?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join a growing community of believers who are transforming lives through intentional discipleship.
          </p>
          <Link
            to="/signup"
            className="inline-block bg-secondary text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
          >
            Get Started for Free
          </Link>
        </div>
      </section>
    </div>
  );
};

const featuresForIndividuals = [
  {
    title: 'Chart Your Journey',
    description: 'Document key milestones, answered prayers, and pivotal moments in your spiritual walk.',
    icon: ArrowTrendingUpIcon,
  },
  {
    title: 'Share Your Story',
    description: 'Privately share testimonies with your community and mentor, giving glory to God for His work in your life.',
    icon: BookOpenIcon,
  },
  {
    title: 'Connect with a Mentor',
    description: 'Receive personal guidance, prayer, and encouragement from a trusted mentor in your community.',
    icon: UserIcon,
  },
];

const featuresForCommunities = [
  {
    title: 'Unify Your People',
    description: 'Provide a central, private hub for your church or ministry to connect and share what God is doing.',
    icon: UsersIcon,
  },
  {
    title: 'Equip Your Mentors',
    description: 'Give your leaders the tools to guide disciples effectively with reminders, notes, and progress tracking.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: 'Foster a Culture of Testimony',
    description: 'Encourage members to share stories of faith, creating a powerful stream of encouragement and praise.',
    icon: ShieldCheckIcon,
  },
];

const testimonials = [
  {
    quote: "This tool has been revolutionary for our church. We're seeing more authentic connections and our leaders feel equipped to disciple effectively.",
    name: "Pastor David Chen",
    role: "Lead Pastor, New Hope Fellowship"
  },
  {
    quote: "As a small group leader, keeping track of everyone's journey was tough. Now, I can be more present and intentional with each person I'm mentoring.",
    name: "Maria Garcia",
    role: "Small Group Leader"
  },
  {
    quote: "Writing down my spiritual journey has been so powerful. It helps me see God's faithfulness over time and share it with others.",
    name: "James Williams",
    role: "Community Member"
  },
];

export default LandingPage;
